import type { CoachActionId, CoachActionOutcome } from './actions';
import type { CoachEvent } from './events';
import type { CoachCondition, CoachJourney, CoachWork } from './schema';
import {
  applyAction,
  applyChoice,
  applyEvent,
  applyForm,
  applyWorkOutcome,
  applyTimeout,
  beginStep,
  checkWait,
  checkWhen,
  eventSubject,
  getStep,
  markWorked,
  pendingWork,
  resolveWork,
  resumeRun,
  startRun,
  type CoachContext,
  type CoachEndReason,
  type CoachMove,
  type CoachRun,
} from './engine';

/** How long a step's work waits for its board's worker to come back before it fails. */
const WORKER_WAIT_MS = 8000;

export interface CoachStorage {
  load(): unknown;
  save(run: CoachRun | null): void;
}

export type CoachChange =
  | { type: 'started'; run: CoachRun }
  | { type: 'step'; run: CoachRun; from: string }
  | { type: 'ended'; run: CoachRun; reason: CoachEndReason }
  | { type: 'worked'; run: CoachRun; action: CoachActionId; ok: boolean; error?: string };

/**
 * Does a step's work on the board open in this tab. The app registers one while a board is
 * open (`setWorker`); work for a run bound to another board waits until that board opens.
 */
export interface CoachWorker {
  boardId: string;
  run(
    action: CoachActionId,
    input: Record<string, unknown>,
    context: CoachContext
  ): Promise<CoachActionOutcome>;
}

/**
 * Why a step's work stopped, for the app to word:
 * - `refused`: the app said no, in its own words (`error`).
 * - `broke`: the app threw.
 * - `missing`: the journey lacked something the work needs (a step it depends on was skipped).
 * - `interrupted`: the page went away while it ran; whether it landed is unknown.
 */
export type CoachFailure = 'refused' | 'broke' | 'missing' | 'interrupted';

/** The current step's work: running, or failed with no `onError` to go to. */
export type CoachActivity =
  | { stepId: string; status: 'working' }
  | { stepId: string; status: 'failed'; reason: CoachFailure; error: string | null };

export interface CoachStoreOptions {
  journeys: readonly CoachJourney[];
  storage?: CoachStorage;
  /** Runs older than this are dropped when restored. */
  maxAgeMs?: number;
  now?: () => number;
  onChange?: (change: CoachChange) => void;
}

export interface CoachStore {
  getRun(): CoachRun | null;
  getJourney(): CoachJourney | null;
  subscribe(listener: () => void): () => void;
  start(journeyId: string, context?: CoachContext): void;
  /**
   * Starts the current journey again from its first step, on the same board. Returns what the
   * run had added to the board, for the app to take away (empty when nothing runs).
   */
  restart(): string[];
  emit(event: CoachEvent): void;
  act(): void;
  /** Picks one of the current card's choices, by its index. */
  choose(index: number): void;
  end(reason: CoachEndReason): void;
  /** Answers the current card's form with the words typed. */
  submit(value: string): void;
  /** Runs the current step's failed work again, from the one that failed. */
  retry(): void;
  getActivity(): CoachActivity | null;
  /** Registers the worker for the open board. Returns a function that removes it. */
  setWorker(worker: CoachWorker): () => void;
  /** What the app knows about the person and the screen, for steps' `when`. */
  setFacts(facts: CoachCondition): void;
}

const ID_LISTS = ['settled', 'created'] as const;

function isRun(value: unknown): value is CoachRun {
  if (!value || typeof value !== 'object') return false;
  const run = value as Partial<CoachRun>;
  const optionalString = (field: unknown) => field === undefined || typeof field === 'string';
  return (
    typeof run.journeyId === 'string' &&
    typeof run.version === 'number' &&
    typeof run.stepId === 'string' &&
    typeof run.startedAt === 'number' &&
    !!run.context &&
    typeof run.context === 'object' &&
    optionalString(run.worked) &&
    optionalString(run.landed) &&
    optionalString(run.checked) &&
    ID_LISTS.every(list => {
      const ids = run[list];
      return ids === undefined || (Array.isArray(ids) && ids.every(id => typeof id === 'string'));
    })
  );
}

/**
 * Holds the one active run. Product code emits events into it from anywhere;
 * the renderer subscribes. Only one journey runs at a time: starting another
 * replaces it.
 */
export function createCoachStore({
  journeys,
  storage,
  maxAgeMs = 24 * 60 * 60 * 1000,
  now = Date.now,
  onChange,
}: CoachStoreOptions): CoachStore {
  const byId = new Map(journeys.map(journey => [journey.id, journey]));
  const listeners = new Set<() => void>();
  let run: CoachRun | null | undefined;
  // Freshness: the order in which each event subject was first seen, against
  // the order position at which the current step began. In memory only.
  const firstSeen = new Map<string, number>();
  let sequence = 0;
  let stepBegan = 0;
  let worker: CoachWorker | null = null;
  let activity: CoachActivity | null = null;
  // Which of the step's works failed, for Try again to pick up from there.
  let failedAt = 0;
  let facts: CoachCondition = {};
  // The current step's `waitFor.atMost`: the one timer here. Nothing reports "processing never
  // finished", so the time running out is the event.
  let waitLimit: ReturnType<typeof setTimeout> | null = null;

  /**
   * Where Try again picks an interrupted step up: the first work that has not landed. A work
   * that binds what it made (a pasted group's id) landed when all of that is in context: the
   * run is saved after each work, so what came back before the page went away is there, and
   * is not made twice. A work that binds nothing cannot tell, so it is done again from there.
   */
  const firstUnlanded = (works: readonly CoachWork[], context: CoachRun['context']) => {
    const index = works.findIndex(work => {
      const bound = Object.values(work.bind ?? {});
      return bound.length === 0 || bound.some(key => context[key] === undefined);
    });
    return index === -1 ? works.length : index;
  };

  const journeyOf = (active: CoachRun | null | undefined) =>
    active ? (byId.get(active.journeyId) ?? null) : null;

  const restore = (): CoachRun | null => {
    const stored = storage?.load();
    if (!isRun(stored)) return null;
    const journey = byId.get(stored.journeyId);
    const age = now() - stored.startedAt;
    if (!journey || journey.version !== stored.version || age < 0 || age > maxAgeMs) return null;
    const resumed = resumeRun(journey, stored);
    // The page went away while this step's work ran: whether it landed is unknown, so the
    // person chooses to run it again rather than it running twice on its own. Work that all
    // landed before the reload is simply done.
    const works = resumed ? getStep(journey, resumed.stepId)?.do : undefined;
    if (
      resumed &&
      resumed.worked === resumed.stepId &&
      resumed.landed !== resumed.stepId &&
      works
    ) {
      activity = { stepId: resumed.stepId, status: 'failed', reason: 'interrupted', error: null };
      failedAt = firstUnlanded(works, resumed.context);
    }
    return resumed;
  };

  const current = (): CoachRun | null => {
    if (run === undefined) {
      run = restore();
      if (!run) storage?.save(null);
    }
    return run;
  };

  const notify = () => listeners.forEach(listener => listener());

  /** Moves past a step's `waitFor` once its `atMost` runs out, if the run is still there. */
  const limitWait = (journey: CoachJourney, begun: CoachRun) => {
    if (waitLimit) clearTimeout(waitLimit);
    waitLimit = null;
    const waitFor = getStep(journey, begun.stepId)?.waitFor;
    if (!waitFor?.atMost) return;
    waitLimit = setTimeout(() => {
      waitLimit = null;
      const latest = current();
      if (latest && latest.journeyId === journey.id && latest.stepId === begun.stepId)
        apply(applyTimeout(journey, latest));
    }, waitFor.atMost * 1000);
  };

  /** A new step begins (or the run ends): freshness and its work's status start over. */
  const publish = (next: CoachRun | null, change: CoachChange | null) => {
    const journey = journeyOf(next);
    run = next && journey ? beginStep(journey, next) : next;
    stepBegan = sequence;
    activity = null;
    if (run && journey) limitWait(journey, run);
    else if (waitLimit) {
      clearTimeout(waitLimit);
      waitLimit = null;
    }
    storage?.save(run);
    if (change) onChange?.(change);
    notify();
    settle();
  };

  /** The same step with more in its context (an answer, what its work returned). */
  const keep = (next: CoachRun) => {
    run = next;
    storage?.save(next);
    notify();
    settle();
  };

  const apply = (move: CoachMove) => {
    const previous = current();
    if (!move || !previous) return;
    if ('end' in move) publish(null, { type: 'ended', run: previous, reason: move.end });
    else if ('stay' in move) keep(move.stay);
    else publish(move.run, { type: 'step', run: move.run, from: previous.stepId });
  };

  /**
   * What a step does by itself once it has begun or learned something: skips when its `when`
   * does not hold, moves on when what it waits for has finished, begins its work.
   */
  function settle() {
    const active = current();
    const journey = journeyOf(active);
    if (!active || !journey) return;
    const decided = checkWhen(journey, active, facts) ?? checkWait(journey, active);
    if (decided) return apply(decided);
    const works = pendingWork(journey, active);
    if (works && activity?.status !== 'working') beginWork(journey, active, works, 0);
  }

  const setActivity = (next: CoachActivity | null) => {
    activity = next;
    notify();
  };

  const workerWaiters = new Set<() => void>();
  /** The worker, when it is there for this board. */
  const currentWorker = (boardId: unknown) =>
    worker && (!boardId || worker.boardId === boardId) ? worker : null;
  /** The worker for this board once it is back (within a few seconds), or null. */
  function workerFor(boardId: unknown): Promise<CoachWorker | null> {
    const fits = () => currentWorker(boardId);
    return new Promise(resolve => {
      const check = () => {
        const next = fits();
        if (!next) return;
        workerWaiters.delete(check);
        clearTimeout(limit);
        resolve(next);
      };
      const limit = setTimeout(() => {
        workerWaiters.delete(check);
        resolve(null);
      }, WORKER_WAIT_MS);
      workerWaiters.add(check);
    });
  }

  /** One work, through the worker, as an outcome: never a throw. */
  async function doOne(
    work: CoachWork,
    context: CoachContext
  ): Promise<{ outcome: CoachActionOutcome; failure?: CoachFailure; detail?: string }> {
    const resolved = resolveWork(work, context);
    if (!resolved.ok)
      return { outcome: { ok: false, error: '' }, failure: 'missing', detail: resolved.error };
    try {
      // The board's worker can step away for a moment while the board finishes loading (its
      // host remounts): wait for it to come back for this board rather than fail the step.
      const ready = currentWorker(context.boardId) ?? (await workerFor(context.boardId));
      if (!ready)
        return { outcome: { ok: false, error: '' }, failure: 'broke', detail: 'no worker' };
      const outcome = await ready.run(resolved.action, resolved.input, context);
      return outcome.ok ? { outcome } : { outcome, failure: 'refused' };
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      return { outcome: { ok: false, error: '' }, failure: 'broke', detail };
    }
  }

  /**
   * Does a step's works in order on the open board, from `from`, marking the step's work begun
   * first so it never runs twice. Stops at the first failure, or once the run has moved on.
   */
  function beginWork(journey: CoachJourney, active: CoachRun, works: CoachWork[], from: number) {
    if (!worker || active.context.boardId !== worker.boardId) return;
    const stepId = active.stepId;
    const begun = markWorked(active);
    run = begun;
    storage?.save(begun);
    setActivity({ stepId, status: 'working' });
    const onStep = () => {
      const latest = current();
      return latest && latest.journeyId === journey.id && latest.stepId === stepId ? latest : null;
    };
    void (async () => {
      for (let index = from; index < works.length; index += 1) {
        const before = onStep();
        if (!before) return;
        const work = works[index]!;
        const { outcome, failure, detail } = await doOne(work, before.context);
        const latest = onStep();
        if (!latest) return;
        onChange?.({
          type: 'worked',
          run: latest,
          action: work.action,
          ok: outcome.ok,
          ...(outcome.ok ? {} : { error: detail ?? (outcome.ok ? '' : outcome.error) }),
        });
        const move = applyWorkOutcome(journey, latest, stepId, index, outcome);
        if (!move) {
          failedAt = index;
          const error = failure === 'refused' && !outcome.ok ? outcome.error : null;
          return setActivity({ stepId, status: 'failed', reason: failure ?? 'broke', error });
        }
        if (!('stay' in move)) return apply(move); // `then` or `onError`: a new step begins.
        run = move.stay;
        storage?.save(move.stay);
      }
      // Every work done; the step stays, waiting for its events.
      const done = onStep();
      if (!done) return;
      activity = null;
      keep({ ...done, landed: stepId });
    })();
  }

  return {
    getRun: current,
    getJourney: () => journeyOf(current()),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start(journeyId, context = {}) {
      const journey = byId.get(journeyId);
      if (!journey) throw new Error(`Unknown coach journey "${journeyId}"`);
      // A different journey still running ends first (dismissed), so how it ended is kept and
      // reported like any other end; starting another never silently drops it. Starting the
      // same journey again is a replay, not a skip.
      const previous = current();
      if (previous && previous.journeyId !== journeyId)
        publish(null, { type: 'ended', run: previous, reason: 'dismissed' });
      const next = startRun(journey, context, now());
      publish(next, { type: 'started', run: next });
    },
    restart() {
      const active = current();
      const journey = journeyOf(active);
      if (!active || !journey) return [];
      const created = active.created ?? [];
      const boardId = active.context.boardId;
      const next = startRun(journey, typeof boardId === 'string' ? { boardId } : {}, now());
      publish(next, { type: 'started', run: next });
      return created;
    },
    emit(event) {
      const subject = eventSubject(event);
      if (subject && !firstSeen.has(subject)) firstSeen.set(subject, ++sequence);
      const active = current();
      const journey = journeyOf(active);
      if (!active || !journey) return;
      const fresh = subject ? firstSeen.get(subject)! > stepBegan : true;
      apply(applyEvent(journey, active, event, fresh));
    },
    act() {
      const active = current();
      const journey = journeyOf(active);
      if (active && journey) apply(applyAction(journey, active));
    },
    choose(index) {
      const active = current();
      const journey = journeyOf(active);
      if (active && journey) apply(applyChoice(journey, active, index));
    },
    end(reason) {
      const active = current();
      if (active) publish(null, { type: 'ended', run: active, reason });
    },
    submit(value) {
      const active = current();
      const journey = journeyOf(active);
      if (!active || !journey || activity?.status === 'working') return;
      apply(applyForm(journey, active, value));
    },
    retry() {
      const active = current();
      const journey = journeyOf(active);
      const works = active && journey ? getStep(journey, active.stepId)?.do : undefined;
      if (!active || !journey || !works || activity?.status !== 'failed') return;
      if (activity.stepId === active.stepId) beginWork(journey, active, works, failedAt);
    },
    getActivity() {
      return activity;
    },
    setWorker(next) {
      worker = next;
      for (const check of [...workerWaiters]) check();
      settle();
      return () => {
        if (worker === next) worker = null;
      };
    },
    setFacts(next) {
      facts = next;
      settle();
    },
  };
}
